import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Save, RotateCcw, Globe, Search, Share2, Upload, Eye } from "lucide-react";
import api, { BACKEND, mergeSaveCmsField } from "@/lib/api";
import { useCms } from "@/lib/cms";
import { Header } from "./Schools";

/* ── helpers ──────────────────────────────────────────────────────────────── */
function abs(url) {
  if (!url) return "";
  return url.startsWith("http") ? url : `${BACKEND}${url}`;
}

/* ── tiny UI primitives ───────────────────────────────────────────────────── */
function Card({ title, subtitle, children }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
      {title && (
        <div className="px-5 py-3.5 border-b border-gray-100 bg-gray-50/60">
          <div className="text-sm font-semibold text-gray-700">{title}</div>
          {subtitle && <div className="text-xs text-gray-500 mt-0.5">{subtitle}</div>}
        </div>
      )}
      <div className="p-5 space-y-4">{children}</div>
    </div>
  );
}

function Field({ label, required, hint, children }) {
  return (
    <div className="space-y-1">
      <label className="block text-xs font-semibold text-gray-700">
        {label}{required && <span className="text-rose-500 ml-0.5">*</span>}
      </label>
      {children}
      {hint && <p className="text-[11px] text-gray-400">{hint}</p>}
    </div>
  );
}

function Input({ ...props }) {
  return (
    <input
      {...props}
      className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-indigo-400/30 focus:border-indigo-400 transition bg-white"
    />
  );
}

function TextArea({ rows = 3, ...props }) {
  return (
    <textarea
      rows={rows}
      {...props}
      className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-indigo-400/30 focus:border-indigo-400 transition bg-white resize-y"
    />
  );
}

function Row2({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>;
}

/* ── image upload field ───────────────────────────────────────────────────── */
function ImageUpload({ label, hint, value, onChange }) {
  const ref = useRef();
  const [uploading, setUploading] = useState(false);

  async function handleFile(file) {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/cms/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  }

  return (
    <Field label={label} hint={hint}>
      <div className="flex items-center gap-3 flex-wrap">
        {value && (
          <div className="relative w-12 h-12 rounded-lg border border-gray-200 overflow-hidden bg-gray-50 shrink-0">
            <img src={abs(value)} alt="" className="w-full h-full object-contain p-1" />
            <button
              onClick={() => onChange("")}
              className="absolute -top-1 -right-1 w-4 h-4 bg-rose-500 text-white rounded-full text-[10px] flex items-center justify-center leading-none"
            >×</button>
          </div>
        )}
        <button
          onClick={() => ref.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-1.5 text-sm border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 px-3 py-2 rounded-lg transition disabled:opacity-50"
        >
          <Upload className="w-3.5 h-3.5" />
          {uploading ? "Uploading…" : value ? "Replace" : "Upload"}
        </button>
        {value && (
          <span className="text-xs text-gray-400 truncate max-w-[160px]">{value.split("/").pop()}</span>
        )}
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={e => handleFile(e.target.files?.[0])} />
      </div>
    </Field>
  );
}

/* ── google search preview ────────────────────────────────────────────────── */
function GooglePreview({ title, url, desc }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 max-w-lg">
      <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
        <span className="w-4 h-4 rounded-full bg-gray-200 inline-block" />
        {url || "www.yourwebsite.com"}
      </div>
      <div className="text-[#1a0dab] text-lg leading-tight font-normal hover:underline cursor-pointer">
        {title || "Your Website Title"}
      </div>
      <div className="text-sm text-[#545454] mt-1 leading-snug line-clamp-2">
        {desc || "Your meta description will appear here. Make it compelling — this is what users see in Google results."}
      </div>
    </div>
  );
}

/* ── og/twitter preview ───────────────────────────────────────────────────── */
function OgPreview({ title, desc, image, site }) {
  return (
    <div className="rounded-lg border border-gray-200 overflow-hidden max-w-sm bg-white">
      {image
        ? <img src={abs(image)} alt="" className="w-full h-40 object-cover" />
        : <div className="w-full h-40 bg-gradient-to-br from-indigo-50 to-gray-100 flex items-center justify-center text-gray-300 text-xs">OG image</div>
      }
      <div className="p-3 border-t border-gray-100">
        <div className="text-[10px] uppercase text-gray-400 tracking-wide">{site || "yourwebsite.com"}</div>
        <div className="text-sm font-semibold text-gray-800 mt-0.5 line-clamp-1">{title || "Page Title"}</div>
        <div className="text-xs text-gray-500 mt-0.5 line-clamp-2">{desc || "Description shown when sharing on social media."}</div>
      </div>
    </div>
  );
}

/* ── main component ───────────────────────────────────────────────────────── */
const EMPTY = {
  company_name: "", short_company_name: "", website_title: "", tagline: "",
  seo_company_name: "",  // ← SEO / metadata name — independent from official company_name
  website_url: "", copyright_text: "", footer_text: "", org_type: "Organization",
  logo_url: "", favicon_url: "", apple_touch_icon_url: "", schema_logo_url: "",
  contact_email: "", contact_phone: "", address: "",
  seo_title: "", seo_meta_description: "", seo_meta_keywords: "",
  seo_og_title: "", seo_og_description: "", seo_og_image: "",
  seo_twitter_title: "", seo_twitter_description: "", seo_twitter_image: "",
  seo_robots: "index, follow", seo_author: "", seo_publisher: "", seo_theme_color: "#0F172A",
  // Analytics & Verification
  google_analytics_id: "", google_tag_manager_id: "", facebook_pixel_id: "",
  google_site_verification: "", bing_site_verification: "", canonical_url: "",
};

export default function WebsiteIdentity() {
  const { reload } = useCms();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("identity");
  // Pristine snapshot from the initial load — lets save() compute exactly
  // which fields this page changed.
  const baselineRef = useRef(null);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/cms/site");
      setForm(prev => {
        const next = { ...EMPTY, ...prev, ...data };
        baselineRef.current = next;
        return next;
      });
    } catch {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []); // eslint-disable-line

  function set(k, v) { setForm(p => ({ ...p, [k]: v })); }

  // Merge only the fields this page actually changed onto the latest live
  // document, instead of overwriting the whole record with this session's
  // possibly-stale copy — prevents an unrelated Website Identity save from
  // resetting header/nav settings edited elsewhere.
  async function save() {
    setSaving(true);
    try {
      const merged = await mergeSaveCmsField("/cms/site", form, baselineRef.current);
      setForm(prev => ({ ...EMPTY, ...prev, ...merged }));
      baselineRef.current = merged;
      reload();
      toast.success("Identity settings saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  function reset() {
    if (window.confirm("Discard unsaved changes?")) load();
  }

  const tabs = [
    { id: "identity",  label: "Company Identity" },
    { id: "seo",       label: "SEO" },
    { id: "og",        label: "Open Graph" },
    { id: "icons",     label: "Icons & Logos" },
    { id: "analytics", label: "Analytics & Tracking" },
    { id: "preview",   label: "Preview" },
  ];

  if (loading) {
    return (
      <div className="fade-up">
        <Header title="Website Identity" subtitle="Dynamic company branding configuration." />
        <div className="mt-6 space-y-4 max-w-2xl">
          {[1,2,3].map(n => <div key={n} className="h-40 rounded-2xl bg-gray-100 animate-pulse" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="fade-up pb-12">
      <Header
        title="Website Identity"
        subtitle="Single source of truth for company name, SEO, Open Graph, favicons, and all email branding."
        actions={
          <div className="flex flex-wrap gap-2">
            <button onClick={reset} className="inline-flex items-center gap-1.5 border border-gray-200 bg-white text-gray-700 text-sm font-medium px-4 py-2 rounded-lg hover:bg-gray-50 transition">
              <RotateCcw className="w-4 h-4" /> Reset
            </button>
            <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 bg-[#0F172A] text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-[#1e293b] transition disabled:opacity-60">
              <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
            </button>
          </div>
        }
      />

      {/* Tabs */}
      <div className="flex gap-1 mt-6 border-b border-gray-200 overflow-x-auto">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`shrink-0 px-4 py-2.5 text-sm font-medium border-b-2 transition ${
              tab === t.id
                ? "border-indigo-600 text-indigo-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >{t.label}</button>
        ))}
      </div>

      <div className="mt-5 space-y-5 max-w-2xl">

        {/* ── Company Identity ── */}
        {tab === "identity" && (<>
          <Card title="Company Name" subtitle="Used in every email, certificate, PDF, and browser title.">
            <Row2>
              <Field label="Company Name" required hint="Full official name — appears in emails, certificates, PDFs.">
                <Input value={form.company_name} onChange={e => set("company_name", e.target.value)} placeholder="Manthan Foundation" />
              </Field>
              <Field label="Short Name" hint="Abbreviation for tight spaces (mobile header, favicon label).">
                <Input value={form.short_company_name} onChange={e => set("short_company_name", e.target.value)} placeholder="Manthan" />
              </Field>
            </Row2>
            <Row2>
              <Field label="SEO Company Name" hint="Name used in OG tags and search snippets. Leave blank to use Company Name above.">
                <Input value={form.seo_company_name} onChange={e => set("seo_company_name", e.target.value)} placeholder={form.company_name || "Manthan Foundation"} />
              </Field>
              <Field label="Tagline" hint="Shown under the logo and in SEO titles.">
                <Input value={form.tagline} onChange={e => set("tagline", e.target.value)} placeholder="Empowering Teachers" />
              </Field>
            </Row2>
            <Row2>
              <Field label="Website Title" hint="Default browser tab title for every page.">
                <Input value={form.website_title} onChange={e => set("website_title", e.target.value)} placeholder="Manthan Foundation | Teacher Training" />
              </Field>
              <Field label="Header Company Name" hint="Set independently in Admin → Header Builder → Company Name tab.">
                <div className="px-3 py-2.5 rounded-lg border border-dashed border-gray-200 text-sm text-gray-400 bg-gray-50">
                  Controlled in Header Builder
                </div>
              </Field>
            </Row2>
          </Card>

          <Card title="Organisation Details">
            <Row2>
              <Field label="Organisation Type" hint="Schema.org type for structured data.">
                <select
                  value={form.org_type || "Organization"}
                  onChange={e => set("org_type", e.target.value)}
                  className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-indigo-400/30 focus:border-indigo-400 bg-white"
                >
                  {["Organization","NGO","EducationalOrganization","LocalBusiness","School","CollegeOrUniversity","GovernmentOrganization"].map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </Field>
              <Field label="Website URL" hint="Canonical domain for JSON-LD and OG tags.">
                <Input type="url" value={form.website_url} onChange={e => set("website_url", e.target.value)} placeholder="https://manthanfoundation.in" />
              </Field>
              <Field label="Contact Email">
                <Input type="email" value={form.contact_email} onChange={e => set("contact_email", e.target.value)} placeholder="info@manthanfoundation.in" />
              </Field>
              <Field label="Contact Phone">
                <Input value={form.contact_phone} onChange={e => set("contact_phone", e.target.value)} placeholder="+91 98450 12345" />
              </Field>
            </Row2>
            <Field label="Address">
              <Input value={form.address} onChange={e => set("address", e.target.value)} placeholder="Bhopal · Mumbai · Delhi" />
            </Field>
          </Card>

          <Card title="Footer & Copyright">
            <Field label="Copyright Text" hint="Appears in the website footer.">
              <Input value={form.copyright_text} onChange={e => set("copyright_text", e.target.value)} placeholder={`© ${new Date().getFullYear()} Manthan Foundation. All rights reserved.`} />
            </Field>
            <Field label="Footer Description" hint="Short tagline shown in the footer below the logo.">
              <TextArea value={form.footer_description} onChange={e => set("footer_description", e.target.value)} placeholder="Empowering schools through innovative teacher training and QR-verified certification programs." />
            </Field>
          </Card>
        </>)}

        {/* ── SEO ── */}
        {tab === "seo" && (<>
          <Card title="Page Title & Description" subtitle="Controls how every page appears in Google.">
            <Field label="SEO Title" hint="Browser tab + Google title. Supports: Company Name | Page">
              <Input value={form.seo_title} onChange={e => set("seo_title", e.target.value)} placeholder="Manthan Foundation | Teacher Training Institute" />
            </Field>
            <Field label="Meta Description" hint="150–160 characters shown under the title in search results.">
              <TextArea rows={2} value={form.seo_meta_description} onChange={e => set("seo_meta_description", e.target.value)} placeholder="Empowering teachers through professional development and QR-verified certification." />
              <p className="text-[11px] text-gray-400">{(form.seo_meta_description || "").length} chars</p>
            </Field>
            <Field label="Keywords" hint="Comma-separated. Secondary signal for SEO.">
              <Input value={form.seo_meta_keywords} onChange={e => set("seo_meta_keywords", e.target.value)} placeholder="teacher training, professional development, certification" />
            </Field>
          </Card>

          <Card title="Advanced SEO">
            <Row2>
              <Field label="Robots" hint="Controls indexing.">
                <select value={form.seo_robots || "index, follow"} onChange={e => set("seo_robots", e.target.value)}
                  className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-indigo-400/30 bg-white">
                  {["index, follow","noindex, follow","index, nofollow","noindex, nofollow"].map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </Field>
              <Field label="Theme Color" hint="Browser toolbar colour on mobile.">
                <div className="flex items-center gap-2">
                  <input type="color" value={form.seo_theme_color || "#0F172A"} onChange={e => set("seo_theme_color", e.target.value)} className="w-10 h-10 rounded border border-gray-200 cursor-pointer" />
                  <Input value={form.seo_theme_color || "#0F172A"} onChange={e => set("seo_theme_color", e.target.value)} />
                </div>
              </Field>
              <Field label="Author">
                <Input value={form.seo_author} onChange={e => set("seo_author", e.target.value)} placeholder="Manthan Foundation" />
              </Field>
              <Field label="Publisher">
                <Input value={form.seo_publisher} onChange={e => set("seo_publisher", e.target.value)} placeholder="Manthan Foundation" />
              </Field>
            </Row2>
          </Card>

          <Card title="Google Search Preview">
            <GooglePreview
              title={form.seo_title || (form.tagline ? `${form.seo_company_name || form.company_name} | ${form.tagline}` : (form.seo_company_name || form.company_name))}
              url={form.website_url}
              desc={form.seo_meta_description}
            />
          </Card>
        </>)}

        {/* ── Open Graph / Twitter ── */}
        {tab === "og" && (<>
          <Card title="Open Graph" subtitle="Controls link previews on WhatsApp, LinkedIn, Facebook.">
            <Field label="OG Title" hint="Defaults to SEO Title if blank.">
              <Input value={form.seo_og_title} onChange={e => set("seo_og_title", e.target.value)} placeholder={form.seo_title || form.seo_company_name || form.company_name} />
            </Field>
            <Field label="OG Description" hint="Defaults to Meta Description if blank.">
              <TextArea rows={2} value={form.seo_og_description} onChange={e => set("seo_og_description", e.target.value)} placeholder={form.seo_meta_description} />
            </Field>
            <ImageUpload label="OG Image" hint="1200×630px recommended. Shown when sharing links." value={form.seo_og_image} onChange={v => set("seo_og_image", v)} />
          </Card>

          <Card title="Twitter / X Card">
            <Field label="Twitter Title" hint="Defaults to OG Title if blank.">
              <Input value={form.seo_twitter_title} onChange={e => set("seo_twitter_title", e.target.value)} placeholder={form.seo_og_title || form.seo_title || form.seo_company_name || form.company_name} />
            </Field>
            <Field label="Twitter Description">
              <TextArea rows={2} value={form.seo_twitter_description} onChange={e => set("seo_twitter_description", e.target.value)} placeholder={form.seo_og_description || form.seo_meta_description} />
            </Field>
            <ImageUpload label="Twitter Image" hint="1200×600px recommended." value={form.seo_twitter_image} onChange={v => set("seo_twitter_image", v)} />
          </Card>

          <Card title="Social Share Preview">
            <p className="text-xs text-gray-500 mb-2">WhatsApp / LinkedIn / Facebook</p>
            <OgPreview
              title={form.seo_og_title || form.seo_title || form.company_name}
              desc={form.seo_og_description || form.seo_meta_description}
              image={form.seo_og_image}
              site={form.website_url?.replace(/https?:\/\//, "") || ""}
            />
          </Card>
        </>)}

        {/* ── Icons & Logos ── */}
        {tab === "icons" && (<>
          <Card title="Logos" subtitle="Uploaded images are served from /api/uploads/. No build required after upload.">
            <ImageUpload label="Primary Logo" hint="Used in header, emails, and certificates." value={form.logo_url} onChange={v => set("logo_url", v)} />
            <ImageUpload label="Schema Logo" hint="Used in JSON-LD structured data (Google). Min 112×112px." value={form.schema_logo_url} onChange={v => set("schema_logo_url", v)} />
          </Card>

          <Card title="Favicon & Icons" subtitle="No rebuild required. Changes take effect immediately after save.">
            <ImageUpload
              label="Favicon"
              hint="Browser tab icon. PNG or ICO, 32×32 or 64×64px. Automatically used as PWA icon."
              value={form.favicon_url}
              onChange={v => set("favicon_url", v)}
            />
            <ImageUpload
              label="Apple Touch Icon"
              hint="Icon when saved to iOS home screen. 180×180px PNG."
              value={form.apple_touch_icon_url}
              onChange={v => set("apple_touch_icon_url", v)}
            />
            {(form.favicon_url || form.logo_url) && (
              <div className="flex items-center gap-4 pt-2">
                <div className="text-xs text-gray-500">Preview:</div>
                <div className="flex items-center gap-2 bg-gray-100 rounded-lg px-3 py-1.5">
                  <img src={abs(form.favicon_url || form.logo_url)} alt="favicon" className="w-4 h-4 object-contain" />
                  <span className="text-xs text-gray-600">{form.company_name || "Your Site"}</span>
                </div>
              </div>
            )}
          </Card>
        </>)}

        {/* ── Analytics & Tracking ── */}
        {tab === "analytics" && (<>
          <Card title="Google Analytics (GA4)" subtitle="Paste your Measurement ID. Script loads automatically.">
            <Field label="Google Analytics ID" hint="Format: G-XXXXXXXXXX">
              <Input value={form.google_analytics_id || ""} onChange={e => set("google_analytics_id", e.target.value)} placeholder="G-XXXXXXXXXX" />
            </Field>
          </Card>

          <Card title="Google Tag Manager" subtitle="Paste your GTM container ID.">
            <Field label="GTM Container ID" hint="Format: GTM-XXXXXXX">
              <Input value={form.google_tag_manager_id || ""} onChange={e => set("google_tag_manager_id", e.target.value)} placeholder="GTM-XXXXXXX" />
            </Field>
          </Card>

          <Card title="Facebook Pixel" subtitle="Paste your Pixel ID.">
            <Field label="Facebook Pixel ID" hint="Numbers only, e.g. 1234567890">
              <Input value={form.facebook_pixel_id || ""} onChange={e => set("facebook_pixel_id", e.target.value)} placeholder="1234567890" />
            </Field>
          </Card>

          <Card title="Search Console Verification" subtitle="One-time verification meta tags for Google and Bing.">
            <Field label="Google Site Verification" hint="Value from the meta tag Google provides — content value only.">
              <Input value={form.google_site_verification || ""} onChange={e => set("google_site_verification", e.target.value)} placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" />
            </Field>
            <Field label="Bing Site Verification" hint="Value from the msvalidate.01 meta tag Bing provides.">
              <Input value={form.bing_site_verification || ""} onChange={e => set("bing_site_verification", e.target.value)} placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" />
            </Field>
          </Card>

          <Card title="Canonical URL" subtitle="Override the default canonical URL (leave blank to use website URL).">
            <Field label="Canonical Base URL" hint="e.g. https://manthanfoundation.in — current page path is appended automatically.">
              <Input type="url" value={form.canonical_url || ""} onChange={e => set("canonical_url", e.target.value)} placeholder="https://manthanfoundation.in" />
            </Field>
          </Card>
        </>)}

        {/* ── Preview ── */}
        {tab === "preview" && (<>
          <Card title="Browser Tab">
            <div className="flex items-center gap-2 bg-gray-100 rounded-t-lg px-3 py-2 max-w-xs">
              {(form.favicon_url || form.logo_url) && (
                <img src={abs(form.favicon_url || form.logo_url)} alt="" className="w-4 h-4 object-contain shrink-0" />
              )}
              <span className="text-xs text-gray-700 truncate">
                {form.website_title || form.seo_title || (form.tagline ? `${form.company_name} | ${form.tagline}` : form.company_name) || "Your Page Title"}
              </span>
            </div>
          </Card>

          <Card title={<span className="flex items-center gap-1.5"><Search className="w-3.5 h-3.5" />Google Search Result</span>}>
            <GooglePreview
              title={form.seo_title || form.website_title || (form.tagline ? `${form.company_name} | ${form.tagline}` : form.company_name)}
              url={form.website_url}
              desc={form.seo_meta_description}
            />
          </Card>

          <Card title={<span className="flex items-center gap-1.5"><Share2 className="w-3.5 h-3.5" />Social Share Preview</span>}>
            <OgPreview
              title={form.seo_og_title || form.seo_title || form.seo_company_name || form.company_name}
              desc={form.seo_og_description || form.seo_meta_description}
              image={form.seo_og_image || form.logo_url}
              site={form.website_url?.replace(/https?:\/\//, "") || ""}
            />
          </Card>

          <Card title={<span className="flex items-center gap-1.5"><Globe className="w-3.5 h-3.5" />JSON-LD Structured Data</span>}
                subtitle="Generated dynamically. Submitted to Google via structured data.">
            <pre className="text-[11px] bg-gray-50 border border-gray-200 rounded-lg p-3 overflow-x-auto text-gray-600 leading-relaxed">
{JSON.stringify({
  "@context": "https://schema.org",
  "@type": form.org_type || "Organization",
  "name": form.seo_company_name || form.company_name || "",
  "url": form.website_url || window.location.origin,
  "email": form.contact_email || "",
  "telephone": form.contact_phone || "",
  "address": form.address ? { "@type": "PostalAddress", "streetAddress": form.address } : undefined,
}, null, 2)}
            </pre>
          </Card>
        </>)}

        {/* Bottom save */}
        <div className="flex flex-wrap gap-3 pt-1">
          <button onClick={save} disabled={saving} className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 bg-[#0F172A] hover:bg-[#1e293b] text-white text-sm font-semibold px-6 py-2.5 rounded-lg transition disabled:opacity-60">
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Settings"}
          </button>
          <button onClick={reset} className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 text-sm font-medium px-6 py-2.5 rounded-lg transition">
            <RotateCcw className="w-4 h-4" /> Reset
          </button>
        </div>
      </div>
    </div>
  );
}
